import { interactionSettingsStore } from '@fullcalendar/react'
import { MuiThemeProvider } from '@material-ui/core'
import { isEmpty } from 'lodash'
import React from 'react'
import { useSelector } from 'react-redux'

import BaseEntity from 'app/components/BaseEntity'
import BaseModal from 'app/components/BaseModal/BaseModal'
import { DateRangePicker } from 'app/components/generic/DateRangePicker'
import { commonAttributes, tableColumnDefaultOption } from 'app/config'
import constants from 'app/constants'
import { fetchClassroomMomentsData } from 'app/store/actions/communityEntity'
import { validateNotEmpty } from 'app/validators'

import { CommentsTable } from './components/CommentsTable'
import { LikesTable } from './components/LikesTable'
import { VisibilityTable } from './components/VisibilityTable'
import { columns, MOMENT_SUBTYPES, MOMENT_TYPE_FIELD_MAPPINGS } from './config'
import { Link, tableTheme, ModalContent } from './styles'

const modalTableMap = {
  visibility: VisibilityTable,
  comments: CommentsTable,
  likes: LikesTable
}

const Membership = () => {
  const [openTable, setOpenTable] = React.useState({ table: '', props: {} })
  const [expandedRows, setExpandedRows] = React.useState({})
  const toggleRows = (rowsId) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [rowsId]: !prevExpandedRows[rowsId]
    }))
  }
  const momentsData = useSelector(
    (state) => state.communityEntityReducer.momentsData
  )

  const modifiedColumns = React.useMemo(() => {
    if (!momentsData.data.length) return columns

    const updatedColumns = [
      {
        name: 'type',
        label: 'moment type',
        options: {
          ...tableColumnDefaultOption,
          disableFilter: true,
          customBodyRender: (value) => (
            <span>{MOMENT_TYPE_FIELD_MAPPINGS?.[value]?.label ?? value}</span>
          )
        }
      },
      {
        name: 'body',
        label: 'Caption',
        editable: true,
        validate: validateNotEmpty,
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '400px',
              maxWidth: '400px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRenderLite: (dataIndex) => {
            const data = momentsData.data[dataIndex].body
            const isNoCaption = isEmpty(data)
            const truncatedData = data.slice(0, 100)
            const isTruncated = data.length > truncatedData.length
            const isExpanded = expandedRows[dataIndex]
            const displayData = isExpanded ? data : truncatedData

            return (
              <div>
                {isNoCaption ? (
                  'N/A'
                ) : (
                  <span>
                    {displayData.length < 100
                      ? displayData
                      : !isExpanded
                      ? displayData + '...'
                      : displayData}
                    <br />
                    {isTruncated ? (
                      <div style={{ textAlign: 'right' }}>
                        <a
                          href="#"
                          onClick={() => toggleRows(dataIndex)}
                          style={{ textDecoration: 'none', cursor: 'pointer' }}
                        >
                          {isExpanded ? 'show less' : 'show more'}
                        </a>
                      </div>
                    ) : null}
                  </span>
                )}
              </div>
            )
          }
        }
      },
      ...columns.slice(0, 3),
      {
        name: 'observations',
        label: 'moment observations',
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true,
          setCellProps: () => ({
            style: {
              minWidth: '200px',
              maxWidth: '200px',
              whiteSpace: 'pre-wrap'
            }
          }),
          customBodyRenderLite: (dataIndex) => {
            const momentData = momentsData.data[dataIndex]
            const momentType = momentData.type
            const observations =
              momentType === MOMENT_SUBTYPES.FOOD
                ? momentData.food_moments?.[0]
                : momentType === MOMENT_SUBTYPES.NAP
                ? momentData.nap_moments?.[0]
                : momentType === MOMENT_SUBTYPES.TOILET
                ? momentData.toilet_moments?.[0]
                : {}

            const fieldMappings = MOMENT_TYPE_FIELD_MAPPINGS[momentType]

            if (!fieldMappings?.fields) {
              return <span>N/A</span>
            }

            const hasObservations = Object.keys(observations || {}).length > 0
            if (!hasObservations) {
              return <span>N/A</span>
            }
            return (
              <div>
                {Object.entries(fieldMappings?.fields).map(
                  ([fieldKey, fieldConfig]) => {
                    const value = observations[fieldKey]

                    if (!value) return null

                    if (fieldConfig.options) {
                      const option = fieldConfig.options[value]
                      return option ? (
                        <div key={fieldKey}>
                          <strong>{fieldConfig.label}:</strong> {option.label}
                        </div>
                      ) : null
                    }

                    if (fieldConfig.formatValue) {
                      return (
                        <div key={fieldKey}>
                          <strong>{fieldConfig.label}:</strong>{' '}
                          {fieldConfig.formatValue(value)}
                        </div>
                      )
                    }

                    return (
                      <div key={fieldKey}>
                        <strong>{fieldConfig.label}:</strong> {value}
                      </div>
                    )
                  }
                )}
              </div>
            )
          }
        }
      },
      {
        ...commonAttributes.centre,
        name: 'schedules[0].batch.centre.name',
        label: 'Centre',
        editable: false,
        validate: validateNotEmpty,
        options: {
          ...tableColumnDefaultOption.options,
          filterKey: 'centre_name',
          customBodyRenderLite: (dataIndex) => {
            const centreName =
              momentsData.data[dataIndex]?.schedules[0]?.batch.centre.name
            return <span>{centreName}</span>
          }
        }
      },
      ...columns.slice(3)
    ]

    return updatedColumns.concat([
      {
        name: 'schedule_date',
        label: 'Session Date',
        widget: 'datepicker',
        options: {
          ...tableColumnDefaultOption,
          headerWidget: DateRangePicker,
          headerWidgetInputType: 'daterange',
          filterKey: 'schedule_date',
          customBodyRenderLite: (dataIndex) => {
            const sessionDate = momentsData.data[dataIndex]?.schedules[0]?.date
            return <span>{sessionDate}</span>
          }
        }
      },
      {
        name: 'visibility',
        label: 'Visibility',
        options: {
          ...tableColumnDefaultOption,
          customBodyRenderLite: (dataIndex) => {
            const visibility = momentsData.data[dataIndex]?.visibility
            return (
              <span>
                {visibility}
                <Link
                  href="#"
                  onClick={() =>
                    setOpenTable({
                      table: 'visibility',
                      props: {
                        momentId: momentsData.data[dataIndex].uuid,
                        title: `${visibility} students`
                      }
                    })
                  }
                >
                  view students
                </Link>
              </span>
            )
          }
        }
      },
      {
        name: 'view_count',
        label: 'Views',
        editable: false,
        options: {
          ...tableColumnDefaultOption.options,
          disableFilter: true
        }
      },
      {
        name: 'comments_count',
        label: 'Comments',
        options: {
          ...tableColumnDefaultOption,
          disableFilter: true,
          customBodyRenderLite: (dataIndex) => {
            const value = momentsData.data[dataIndex]?.comments_count
            if (value === 0) return <span>{value}</span>
            return (
              <span>
                {value}
                <Link
                  href="#"
                  onClick={() =>
                    setOpenTable({
                      table: 'comments',
                      props: {
                        momentId: momentsData.data[dataIndex].uuid,
                        title: 'Comments'
                      }
                    })
                  }
                >
                  view comments
                </Link>
              </span>
            )
          }
        }
      },
      {
        name: 'likes_count',
        label: 'Likes',
        options: {
          ...tableColumnDefaultOption,
          disableFilter: true,
          customBodyRenderLite: (dataIndex) => {
            const value = momentsData.data[dataIndex]?.likes_count
            if (value === 0) return <span>{value}</span>
            return (
              <span>
                {value}
                <Link
                  href="#"
                  onClick={() =>
                    setOpenTable({
                      table: 'likes',
                      props: {
                        momentId: momentsData.data[dataIndex].uuid,
                        title: `Liked by`
                      }
                    })
                  }
                >
                  view likes
                </Link>
              </span>
            )
          }
        }
      },
      {
        name: 'share_count',
        label: 'Share Count',
        options: {
          ...tableColumnDefaultOption,
          disableFilter: true
        }
      },
      {
        name: 'share_view_count',
        label: 'Share View Count',
        options: {
          ...tableColumnDefaultOption,
          disableFilter: true
        }
      }
    ])
  }, [momentsData, expandedRows])

  const Table = modalTableMap[openTable.table]

  const closeModal = () => {
    setOpenTable({ table: '', props: {} })
  }

  const innerTable = Table && (
    <MuiThemeProvider theme={tableTheme}>
      <Table {...openTable.props} />
    </MuiThemeProvider>
  )

  return (
    <>
      <BaseModal open={!!Table} onCloseModal={closeModal}>
        {innerTable}
      </BaseModal>

      <BaseEntity
        entity={constants.MOMENTS}
        label={constants.MOMENTS}
        columns={modifiedColumns}
        data={momentsData}
        fetchData={fetchClassroomMomentsData}
        readOnly
      />
    </>
  )
}

export default Membership
